/**
 * Policies API Service
 * 
 * Provides access to policy CRUD operations
 */

import { api } from './api';

export interface Policy {
  id: string;
  policy_id: string;
  org_id?: string;
  site_id?: string;
  policy_type:
    | 'ppe_compliance'
    | 'zone_intrusion'
    | 'dwell_violation'
    | 'loitering'
    | 'occupancy'
    | 'worker_down'
    | 'forklift_proximity'
    | 'pedestrian_near_moving_forklift'
    | 'forklift_speeding'
    | 'line_crossing'
    | 'region_entrance'
    | 'region_exiting'
    | 'composite_alert';
  name: string;
  description?: string;
  enabled?: boolean;
  status?: 'draft' | 'active' | 'disabled' | 'deleted';
  scope: {
    zone_ids?: string[];
    zone_tags?: string[];
    camera_ids?: string[];
    entity_types?: string[];
    // Disambiguate camera_ids across multiple edges (pilot guardrail).
    // This is stored in scope JSONB on the backend (not a first-class column).
    edge_device_id?: string;
    // Optional: geometry mode hint (e.g., whole_view vs zones)
    geometry_mode?: string;
  };
  /** Top-level edge_device_id from policies table (returned by API). */
  edge_device_id?: string;
  /** Top-level camera_id from policies table (returned by API). */
  camera_id?: string;
  /** Resolved target from API (edge_device_id, camera_id). */
  target?: { edge_device_id?: string; camera_id?: string };
  config: Record<string, any>;
  timing?: {
    persistence_seconds?: number;
    cooldown_seconds?: number;
    min_dwell_seconds?: number;
    dedup_window_seconds?: number;
    max_incidents_per_hour?: number;
    max_incidents_per_minute_per_camera?: number;
    max_incidents_per_minute_per_camera_per_type?: number;
    capture_evidence_on_repeat?: boolean;
    reuse_open_incident_id?: boolean;
  };
  schedule?: {
    enabled: boolean;
    timezone?: string;
    days?: string[];
    start_time?: string;
    end_time?: string;
    windows?: Record<string, Array<{ start: string; end: string }>>;
    exceptions?: Array<{ start: string; end: string; reason?: string }>;
  };
  alert?: {
    enabled?: boolean;
    severity: 'low' | 'medium' | 'high' | 'critical';
    message_template?: string;
  };
  escalation?: {
    enabled: boolean;
    threshold_count?: number;
    threshold_duration_seconds?: number;
    escalate_to?: string;
  };
  pattern?: {
    enabled: boolean;
    min_events?: number;
    window_seconds?: number;
    cooldown_seconds?: number;
  };
  actions?: Array<{ type: string; enabled?: boolean; [k: string]: any }> | null;
  version?: number;
  created_by?: string;
  created_at?: string;
  updated_by?: string;
  updated_at?: string;
  last_deployment_id?: string;
  last_deployed_at?: string;
  last_ack_at?: string;

  // Phase F: deterministic deployment state (derived from Clarity deployments correlation table)
  deployment_state?: 'DRAFT' | 'DEPLOYING' | 'DEPLOYED' | 'FAILED' | 'FAILED_TIMEOUT';
  deployment_stage?: string;
  deployment_reason_code?: string;
  deployment_reason?: string;
  migration_error_code?: string;
  migration_error_detail?: Record<string, any> | null;
  deployment?: {
    deployment_id?: string;
    status?: string;
    bundle_version?: number;
    bundle_hash?: string;
    published_at?: string;
    acked_at?: string;
    nacked_at?: string;
    timeout_at?: string;
    error?: any;
    [k: string]: any;
  };
}

export interface PolicyCreateData {
  policy_id?: string;
  policy_type: Policy['policy_type'];
  name: string;
  description?: string;
  scope: Policy['scope'];
  config: Record<string, any>;
  timing?: Policy['timing'];
  schedule?: Policy['schedule'];
  alert?: Policy['alert'];
  escalation?: Policy['escalation'];
  pattern?: Policy['pattern'];
  actions?: Policy['actions'];
  enabled?: boolean;
}

export interface PolicyListResponse {
  policies: Policy[];
  count: number;
}

export interface PolicyDeployResponse {
  deployment_id: string;
  deployment_ids_by_device?: Record<string, string>;
  config_type: 'policies';
  bundle_version?: number;
  bundle_hash?: string;
  generated_at?: string;
  site_id?: string;
  target_devices?: string[];
  results?: Record<string, string>;
  policies?: string[];
  status?: string;
  preflight_warnings?: Array<Record<string, any>>;
  arming_warnings?: Array<Record<string, any>>;
}

export const policiesApi = {
  /**
   * List all policies for a site
   */
  listPolicies: async (
    siteId: string = 'default',
    includeDisabled?: boolean,
    moduleScope?: 'ocr' | 'warehouse' | 'shared',
    orgId?: string
  ): Promise<PolicyListResponse> => {
    const params: Record<string, any> = {};
    if (includeDisabled !== undefined) params.include_disabled = includeDisabled;
    if (moduleScope) params.module_scope = moduleScope;
    if (orgId && orgId.trim()) params.org_id = orgId.trim();
    const response = await api.get(`/api/v1/sites/${siteId}/policies`, { params });
    return response.data;
  },

  /**
   * Get policy by ID
   */
  getPolicy: async (siteId: string, policyId: string, orgId?: string): Promise<Policy> => {
    // Backend route is not site-scoped for get/update/delete; include explicit scope for strict tenant middleware.
    const params: Record<string, any> = {};
    if (siteId && String(siteId).trim()) params.site_id = String(siteId).trim();
    if (orgId && String(orgId).trim()) params.org_id = String(orgId).trim();
    const response = await api.get(`/api/v1/policies/${policyId}`, { params });
    return response.data;
  },

  /**
   * Create a new policy
   */
  createPolicy: async (siteId: string, data: PolicyCreateData & { module_scope?: string }): Promise<Policy> => {
    const response = await api.post(`/api/v1/sites/${siteId}/policies`, data);
    return response.data;
  },

  /**
   * Update an existing policy
   */
  updatePolicy: async (siteId: string, policyId: string, data: Partial<PolicyCreateData>, orgId?: string): Promise<Policy> => {
    const params: Record<string, any> = {};
    if (siteId && String(siteId).trim()) params.site_id = String(siteId).trim();
    if (orgId && String(orgId).trim()) params.org_id = String(orgId).trim();
    const response = await api.put(`/api/v1/policies/${policyId}`, data, { params });
    return response.data;
  },

  /**
   * Delete a policy (soft delete)
   */
  deletePolicy: async (siteId: string, policyId: string, orgId?: string): Promise<void> => {
    const params: Record<string, any> = {};
    if (siteId && String(siteId).trim()) params.site_id = String(siteId).trim();
    if (orgId && String(orgId).trim()) params.org_id = String(orgId).trim();
    await api.delete(`/api/v1/policies/${policyId}`, { params });
  },

  /**
   * Delete all policies targeting a given edge device (soft delete)
   */
  deleteAllPoliciesForDevice: async (
    siteId: string,
    edgeDeviceId: string
  ): Promise<{ deleted_count: number; message: string }> => {
    const response = await api.post(
      `/api/v1/sites/${siteId}/policies/delete-all-for-device`,
      { edge_device_id: edgeDeviceId }
    );
    return response.data;
  },

  /**
   * Lint a policy before deploy (A-53)
   */
  lintPolicy: async (siteId: string, policyId: string): Promise<any> => {
    const response = await api.post(`/api/v1/sites/${siteId}/policies/lint`, { policy_id: policyId });
    return response.data;
  },

  /**
   * Deploy policy to edge devices.
   * Epic 11: optional groupId limits deploy to canary cohort.
   */
  deployPolicy: async (
    siteId: string,
    policyId: string,
    options?: { groupId?: string }
  ): Promise<PolicyDeployResponse> => {
    const body: Record<string, unknown> = {
      policy_ids: [policyId],
      preflight_override: true,
      preflight_override_reason: 'ui_policy_deploy_default_override',
    };
    if (options?.groupId) body.group_id = options.groupId;
    const response = await api.post(`/api/v1/sites/${siteId}/policies/deploy`, body);
    return response.data;
  },

  /**
   * Deploy ALL active policies for a site (used by Zone Features MVP).
   * Epic 11: optional groupId limits deploy to canary cohort.
   */
  deployAllPolicies: async (
    siteId: string,
    options?: { groupId?: string }
  ): Promise<PolicyDeployResponse> => {
    const body: Record<string, unknown> = {
      preflight_override: true,
      preflight_override_reason: 'ui_policy_deploy_all_default_override',
    };
    if (options?.groupId) body.group_id = options.groupId;
    const response = await api.post(`/api/v1/sites/${siteId}/policies/deploy`, body);
    return response.data;
  },

  /**
   * Activate a policy (status='active')
   */
  activatePolicy: async (policyId: string, scope?: { site_id?: string; org_id?: string }): Promise<any> => {
    const params: Record<string, any> = {};
    if (scope?.site_id && String(scope.site_id).trim()) params.site_id = String(scope.site_id).trim();
    if (scope?.org_id && String(scope.org_id).trim()) params.org_id = String(scope.org_id).trim();
    const response = await api.post(`/api/v1/policies/${policyId}/activate`, {}, { params });
    return response.data;
  },

  /**
   * Deactivate a policy (status='disabled')
   */
  deactivatePolicy: async (policyId: string, scope?: { site_id?: string; org_id?: string }): Promise<any> => {
    const params: Record<string, any> = {};
    if (scope?.site_id && String(scope.site_id).trim()) params.site_id = String(scope.site_id).trim();
    if (scope?.org_id && String(scope.org_id).trim()) params.org_id = String(scope.org_id).trim();
    const response = await api.post(`/api/v1/policies/${policyId}/deactivate`, {}, { params });
    return response.data;
  },
};
